// @ts-strict-ignore
import {
  Button,
  ButtonVariant,
  IconName,
} from '@usealloy/component-library';
import React, { useCallback, useEffect, useState } from 'react';

import {
  selectDiffMode,
  selectPreviewOutcomeAction,
} from 'flow/store/redux/selectors';
import { removeAction, resetNodeAndConnectorState } from 'flow/store/redux/slice';

import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import * as helpers from './helpers';
import IslandActionRuleEditor from '../../IslandActionRuleEditor';
import type { BaseOutcomeIslandProps } from '../types';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import type { Action } from '~/dashboard/views/workflows/store/engine/types';
import { generatePublicId } from 'flow/service/publicIds';
import BaseComplexIsland from '../../BaseComplexIsland';
import { useEditorAccess } from 'flow/editor/context';
import { getIsEditable } from 'flow/service/general';
import { DiffViewOutcomeActionsSplit } from 'flow/editor/components/DiffViewSplit/DiffViewSplit';


interface ComplexOutcomeIslandProps extends BaseOutcomeIslandProps { }

export default function ComplexOutcomeIsland({
  outcomeActions,
}: ComplexOutcomeIslandProps) {
  const dispatch = useAppDispatch();
  const previewOutcomeAction = useAppSelector(selectPreviewOutcomeAction);
  const isInDiffMode = useAppSelector(selectDiffMode);

  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);

  /**
   * it's possible that this island opens with a preview outcome in-flight, so we initialize
   * these 2 pieces of state with the respective values when applicable
  */
  const [isAddingNewOutcome, setIsAddingNewOutcome] = useState(Boolean(previewOutcomeAction));
  const [
    previewOutcomeActionPublicId, setPreviewOutcomeActionPublicId,
  ] = useState<Action['publicId']>(previewOutcomeAction?.publicId ?? null);

  useEffect(() => {
    setIsAddingNewOutcome(Boolean(previewOutcomeAction));
    if (previewOutcomeAction) {
      setPreviewOutcomeActionPublicId(previewOutcomeAction.publicId);
    }
  }, [previewOutcomeAction]);

  const handleDrawerToggle = useCallback(() => {
    if (previewOutcomeAction) {
      dispatch(removeAction({ id: previewOutcomeAction.publicId }));
    }

    dispatch(resetNodeAndConnectorState());
  }, [dispatch, previewOutcomeAction]);

  const handleOnAddOutcomeClick = useCallback(() => {
    const publicId = generatePublicId('action', ActionType.OUTCOME);
    helpers.handleOnAddOutcomeClick(dispatch, outcomeActions, publicId);
    setIsAddingNewOutcome(true);
    setPreviewOutcomeActionPublicId(publicId);
  }, [dispatch, outcomeActions]);

  const handleOnSubmitAction = useCallback(() => {
    setIsAddingNewOutcome(false);
    setPreviewOutcomeActionPublicId(null);
  }, []);

  const handleOnCancelAction = useCallback(() => {
    setIsAddingNewOutcome(false);
  }, []);

  return (
    <BaseComplexIsland
      onDrawerToggle={handleDrawerToggle}
      title="Outcomes"
      data-dd-privacy="allow"
      isInDiffMode={isInDiffMode}
    >
      {inEditMode && !isInDiffMode && (
        <Button
          disabled={isAddingNewOutcome}
          icon={IconName.Add}
          onClick={handleOnAddOutcomeClick}
          variant={ButtonVariant.tertiary}
          data-dd-action-name="outcome-island-add-outcome"
        >
          Add Outcomes
        </Button>
      )}
      {isInDiffMode ? (
        <DiffViewOutcomeActionsSplit>
          {action => (
            <IslandActionRuleEditor
              action={action}
              actionType={ActionType.OUTCOME}
              allActionsOfActionType={outcomeActions}
              isPreviewAction={previewOutcomeActionPublicId === action.publicId}
              onCancelAction={handleOnCancelAction}
              onSubmitAction={handleOnSubmitAction}
              isInDiffMode={isInDiffMode}
            />
          )}
        </DiffViewOutcomeActionsSplit>
      ) : (
        outcomeActions.map(outcomeAction => (
          <IslandActionRuleEditor
            key={outcomeAction.publicId}
            action={outcomeAction}
            actionType={ActionType.OUTCOME}
            allActionsOfActionType={outcomeActions}
            isPreviewAction={previewOutcomeActionPublicId === outcomeAction.publicId}
            onCancelAction={handleOnCancelAction}
            onSubmitAction={handleOnSubmitAction}
            isInDiffMode={isInDiffMode}
          />
        ))
      )}
    </BaseComplexIsland>
  );
}
